import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Star } from "lucide-react";
import { api, formatZAR } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import TrustBar from "@/components/TrustBar";

const HERO = "https://static.prod-images.emergentagent.com/jobs/61f75580-40da-4cae-8d9a-49fb6b0c2bbd/images/7257656bede964db298447ce7f87f0b0623f5209593372a3d3c413bd25ea84b8.png";
const COL_IMAGES = {
  curly: "https://customer-assets.emergentagent.com/job_glueless-beauty-co/artifacts/hyulzjg2_ChatGPT%20Image%20May%2028%2C%202026%2C%2011_29_31%20PM.png",
  hd_lace: "https://static.prod-images.emergentagent.com/jobs/61f75580-40da-4cae-8d9a-49fb6b0c2bbd/images/7fd0540a004246c39646778e23b105b6a988bfb7592729f0e777b6c4f7539e5b.png",
  straight: "https://static.prod-images.emergentagent.com/jobs/61f75580-40da-4cae-8d9a-49fb6b0c2bbd/images/d66709de59bbde95e642d9734be0fe0a810b43c83a6ca61495ce395efea23fa0.png",
  glueless: "https://customer-assets.emergentagent.com/job_glueless-beauty-co/artifacts/j2odvsut_ChatGPT%20Image%20May%2028%2C%202026%2C%2011_26_18%20PM.png",
  blonde: "https://customer-assets.emergentagent.com/job_glueless-beauty-co/artifacts/8ww2mt21_ChatGPT%20Image%20May%2028%2C%202026%2C%2011_36_41%20PM.png",
  packaging: "https://static.prod-images.emergentagent.com/jobs/61f75580-40da-4cae-8d9a-49fb6b0c2bbd/images/466bbda6acceeec539fe277ce33c965a5d36e04cdbf2a737239ec2963efd842d.png",
};

const TESTIMONIALS = [
  { name: "Thandi M.", city: "Sandton", body: "The HD lace literally disappears into my skin. Had three people ask me about my 'real' hair this week.", rating: 5 },
  { name: "Lerato K.", city: "Cape Town", body: "Glueless install took five minutes. I'm a Maison Luxe customer for life.", rating: 5 },
  { name: "Zinhle B.", city: "Durban", body: "Soft, bouncy, no shedding. The packaging alone made me feel like royalty.", rating: 5 },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [bestSellers, setBestSellers] = useState([]);

  useEffect(() => {
    api.get("/products", { params: { limit: 8, sort: "featured" } }).then((r) => {
      const all = r.data;
      setFeatured(all.filter((p) => p.featured).slice(0, 8));
      setBestSellers(all.filter((p) => p.best_seller).slice(0, 4));
    });
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="grid lg:grid-cols-2 min-h-[78vh] lg:min-h-screen">
          <div className="order-2 lg:order-1 flex items-center px-6 lg:px-16 py-16 lg:py-0 bg-ivory">
            <div className="max-w-xl fade-up">
              <p className="small-label text-gold">New collection · ZA</p>
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl xl:text-7xl mt-5 leading-[1.05]">
                Couture wigs,<br /><em className="text-gold not-italic">undetectable</em> hairlines.
              </h1>
              <p className="mt-6 text-stone text-base leading-relaxed max-w-md">
                Hand-finished HD lace and glueless human-hair wigs, shipped to your door across South Africa.
                Salon quality, without the salon ceremony.
              </p>
              <div className="mt-9 flex flex-wrap gap-3">
                <Link to="/shop" data-testid="hero-shop-wigs-button" className="btn-primary">
                  Shop Wigs <ArrowRight strokeWidth={1.5} className="w-4 h-4 ml-2" />
                </Link>
                <Link to="/collections/glueless" data-testid="hero-glueless-button" className="btn-outline">
                  Glueless Collection
                </Link>
              </div>
              <div className="mt-10 grid grid-cols-3 gap-4">
                {["HD Lace", "Glueless", "Pre-Plucked"].map((t) => (
                  <div key={t} className="text-center border border-stone-line rounded-2xl py-3">
                    <Sparkles strokeWidth={1.5} className="w-3.5 h-3.5 inline mr-1 text-gold" />
                    <span className="text-xs uppercase tracking-luxe">{t}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="order-1 lg:order-2 relative">
            <img src={HERO} alt="Luxury wig editorial" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute bottom-6 right-6 bg-ivory/90 backdrop-blur-md rounded-2xl px-5 py-4 max-w-[220px] hidden md:block">
              <p className="small-label">Best Seller</p>
              <p className="font-serif text-base mt-1 leading-tight">Noir 9x6 HD Wear-&-Go</p>
              <p className="text-sm mt-1">{formatZAR(3499)}</p>
            </div>
          </div>
        </div>
      </section>

      <TrustBar />

      {/* COLLECTIONS BENTO */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="flex items-end justify-between mb-10 gap-4">
          <div>
            <p className="small-label">Shop by texture</p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl mt-3 leading-tight">Collections</h2>
          </div>
          <Link to="/shop" data-testid="view-all-link" className="hidden sm:inline-flex items-center text-xs uppercase tracking-luxe hover:text-gold">
            View All <ArrowRight strokeWidth={1.5} className="w-4 h-4 ml-2" />
          </Link>
        </div>
        <div className="grid grid-cols-12 gap-4 lg:gap-6">
          <CollectionCard slug="hd-lace" title="HD Lace" body="Invisible. Undetectable." image={COL_IMAGES.hd_lace} cls="col-span-12 lg:col-span-8 aspect-[16/10] lg:aspect-[16/10]" />
          <CollectionCard slug="glueless" title="Glueless" body="Slip on. Step out." image={COL_IMAGES.glueless} cls="col-span-12 sm:col-span-6 lg:col-span-4 aspect-square" />
          <CollectionCard slug="blonde" title="Blonde" body="Sun-touched, head-turning." image={COL_IMAGES.blonde} cls="col-span-12 sm:col-span-6 lg:col-span-4 aspect-square" />
          <CollectionCard slug="curly" title="Curly" body="Bold and beautifully defined." image={COL_IMAGES.curly} cls="col-span-12 sm:col-span-6 lg:col-span-4 aspect-square" />
          <CollectionCard slug="beginner-friendly" title="Beginner Friendly" body="Slip on in 5 minutes." image={COL_IMAGES.packaging} cls="col-span-12 sm:col-span-6 lg:col-span-4 aspect-square" />
        </div>
      </section>

      {/* FEATURED */}
      <section className="max-w-7xl mx-auto px-6 pb-24">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="small-label">Featured</p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl mt-3">The Maison Edit</h2>
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 lg:gap-x-8 gap-y-10">
          {featured.map((p, i) => (<ProductCard key={p.id} product={p} index={i} />))}
        </div>
      </section>

      {/* QUOTE / STORY */}
      <section className="bg-nude">
        <div className="max-w-5xl mx-auto px-6 py-24 text-center">
          <p className="small-label text-gold">Our Promise</p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl mt-4 leading-tight">
            "Salon-quality wigs,<br />without the salon pricing."
          </h2>
          <p className="text-stone mt-6 max-w-2xl mx-auto">
            Every unit is hand-finished and quality-checked in our Johannesburg studio before it travels to you,
            wrapped in tissue, sealed with intention.
          </p>
          <Link to="/about" className="btn-outline mt-9">Our Story</Link>
        </div>
      </section>

      {/* BEST SELLERS */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="small-label">Best Sellers</p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl mt-3">Loved Across SA</h2>
          </div>
          <Link to="/collections/best-sellers" className="hidden sm:inline-flex items-center text-xs uppercase tracking-luxe hover:text-gold">
            View All <ArrowRight strokeWidth={1.5} className="w-4 h-4 ml-2" />
          </Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 lg:gap-x-8 gap-y-10">
          {bestSellers.map((p, i) => (<ProductCard key={p.id} product={p} index={i} />))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-charcoal text-ivory">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <p className="small-label text-gold">Real reviews</p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl mt-3">Heard from our community</h2>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t) => (
              <div key={t.name} className="border border-ivory/15 rounded-3xl p-8">
                <div className="flex gap-0.5 text-gold">
                  {Array.from({ length: t.rating }).map((_, i) => (
                    <Star key={i} className="w-3.5 h-3.5 fill-current" />
                  ))}
                </div>
                <p className="mt-4 text-ivory/90 leading-relaxed">"{t.body}"</p>
                <p className="mt-5 small-label text-ivory/70">{t.name} · {t.city}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function CollectionCard({ slug, title, body, image, cls }) {
  return (
    <Link
      to={`/collections/${slug}`}
      data-testid={`collection-card-${slug}`}
      className={`relative overflow-hidden rounded-3xl group ${cls}`}
    >
      <img src={image} alt={title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
      <div className="absolute inset-0 bg-gradient-to-t from-charcoal/55 via-transparent to-transparent" />
      <div className="absolute bottom-6 left-6 right-6 text-ivory">
        <p className="text-[10px] uppercase tracking-luxe text-ivory/80">{body}</p>
        <p className="font-serif text-2xl lg:text-3xl mt-1">{title}</p>
        <p className="text-xs mt-2 underline underline-offset-4">Explore</p>
      </div>
    </Link>
  );
}
